/* One colour per DEVICE, rather than one colour per device CATEGORY.
 *
 * ── The report this exists to answer ──────────────────────────────────────
 * "There should only be one Y value for each X value. But this picture shows
 * that there's multiple line graphs ... part dashed and part solid line that
 * overlap and should not be there."
 *
 * The data behind that screen was correct. Four EV devices were legend-enabled
 * on the Summary page for 01/02/2040, which is seven strokes - each EV that
 * drove in the period draws a solid plug-flow stroke and a dashed driving twin
 * (utils/twinStrokeSeries.js) - and every one of those seven was painted the
 * SAME green, because getSeriesColor answered by category:
 *
 *     if (deviceNames?.EV?.includes(name)) return "#91CC75";
 *
 * Measured on that exact screen, rendered through real ECharts and sampled
 * inside every bezier:
 *
 *   24 UTR C WC4       88% of its stroke lies on y = 0
 *   11 Top Picks Day A 55% on y = 0
 *   6 Top Picks Night B 47% on y = 0
 *   pairwise coincident ink between device strokes: 26% - 45%
 *
 * An idle interval is a real 0 kWh reading, so all of that ink is honest - but
 * a third to a half of each device's line was drawn directly on top of another
 * device's line in an indistinguishable colour, and where n semi-transparent
 * strokes stack, alpha compositing darkens them (1 - (1 - 0.38)^4 = 0.85
 * against 0.38 for one). A stack of same-coloured strokes at zero, some solid
 * and some dashed, is precisely "part dashed and part solid line that overlap",
 * rendered bolder than any single line - which is what was reported.
 *
 * Distinguishing the devices does not change one plotted number.
 *
 * ── The palette is measured, not chosen by eye ────────────────────────────
 * The first attempt at this file picked hues by hand and was wrong in a way no
 * amount of looking would have caught: #C264C9 against #2E9BC9 is ΔE 1.0 under
 * deuteranopia - the same colour to roughly 8% of men - which reproduces the
 * exact defect above for those readers. Every value below is checked with the
 * dataviz skill's scripts/validate_palette.js, which is the only acceptable way
 * to answer this question. Re-run it before changing a single value:
 *
 *   node scripts/validate_palette.js "<hex,...>" --mode light --pairs all
 *
 * USE --pairs all, NOT the default adjacent scope. Adjacent-pair checking suits
 * series that are only ever compared with their neighbours (stacked segments).
 * Here every enabled device is drawn simultaneously over the same axis and any
 * two of them can land on top of each other - the 37% coincidence measured
 * above was between roster positions 0 and 2, which are not adjacent - so the
 * scope that matters is every pair.
 *
 * ── The owner's constraints on the EV sequence ────────────────────────────
 * The EV sequence below is the result of a search over ~400,000 combinations
 * under two constraints set by the owner, both of which narrow the field a long
 * way and neither of which is negotiable by whoever reads this next:
 *
 * 1. NO RED, ORANGE OR YELLOW. Warm hues were excluded outright: they are hard
 *    for the owner to see, and the Summary chart has already spent that half of
 *    the wheel on Consumption (#FF1E1E) and Production Summary (#E6A700), so a
 *    warm device colour would collide with a summary line as well. This costs
 *    real separation and the cost is known: the best minimum EV-to-EV
 *    separation achievable WITHOUT warm hues is ΔE 14.9, against ΔE 16.3 if
 *    warm hues were allowed. Restoring warm hues would buy 1.4 ΔE and is still
 *    the wrong trade - the owner cannot read the result.
 *
 * 2. THE FIRST EV IS A LIGHT GREEN, by request (#5BC840).
 *
 * ── Three purples that differ by LIGHTNESS, not hue ───────────────────────
 * #A36FAF, #E17AF8 and #9628A1 are three purples separated mainly by lightness
 * rather than by hue. That is deliberate and it is forced: with the warm half
 * of the wheel excluded, the cool half does not hold six distinct hues, so the
 * sixth device has to be bought with lightness instead. Anyone who "fixes" this
 * by spreading the three purples out in hue is spending hue the palette does
 * not have and will land back on a warm tone or on a collision with the blues.
 * The measured cost of the lightness-only separation is the worst pair in the
 * whole set: #A36FAF against #9628A1 is ΔE 14.9 in normal vision.
 *
 * ── What was actually measured, including what does NOT clear ─────────────
 * Recorded honestly so that nobody re-derives it, and so that nobody reads a
 * blanket "validated" and assumes every pair is clear. Light mode, --pairs all,
 * across the six EV colours, the battery blue and the four summary lines
 * (Transfer #675AFF, Consumption #FF1E1E, Storage Summary #1B7F5A, Production
 * Summary #E6A700). 45 EV-to-EV, EV-to-battery and EV-to-summary pairs:
 *
 *   Normal vision, floor 15:  44 of 45 pairs clear.
 *     #A36FAF ↔ #9628A1   ΔE 14.9   the lightness-separated purples, above
 *     next worst is #2DAEA0 ↔ Storage Summary at ΔE 15.4, which clears.
 *
 *   Protan/deutan, floor 6:  THREE pairs do not clear.
 *     #9628A1 ↔ battery #0072B2   ΔE 3.2 (deutan)
 *     #5BC840 ↔ Production #E6A700  ΔE 4.4 (deutan)
 *     #A36FAF ↔ battery #0072B2   ΔE 4.0 (protan)
 *     #00A5FF ↔ #E17AF8           ΔE 4.3 (protan)
 *   and one sits in the 6-8 band that is legal only with secondary encoding:
 *     #2DAEA0 ↔ #A36FAF           ΔE 7.9 (deutan)
 *
 *   Tritan (informational - the validator does not gate on it, and tritanopia
 *   is ~0.01% of people): weakest are #2DAEA0 ↔ #00A5FF at 4.4 and
 *   battery #0072B2 ↔ Storage Summary at 3.1.
 *
 * The #5BC840 ↔ Production pair is the price of constraint 2, and it is a
 * deliberate trade rather than an oversight. Production was #C46A00 (orange)
 * and measured ΔE 3.1 deutan / 14.4 normal against Consumption #FF1E1E - two
 * summary lines that are BOTH ON BY DEFAULT, so every reader met that failure
 * on first load. Gold moves that pair to 12.4 / 23.9 and clears it. Gold and
 * green are the canonical deuteranopia collision, so the cost lands here
 * instead: a sweep of every in-band gold found NONE clearing red at 8 and
 * #5BC840 at 6 simultaneously - gold sits between red and green on the axis
 * deuteranopia collapses, so it cannot escape both. The failure that remains
 * needs a reader to toggle an individual EV onto the Summary overlay next to
 * Production, which is strictly rarer than the default view. Moving the first
 * EV off green would clear it; the owner asked for the green.
 *
 * The three protan/deutan failures are the price of constraint 1: purple and
 * blue converge under red-green CVD, and with warm hues excluded the palette is
 * mostly purples and blues. They are ACCEPTED here rather than solved, for two
 * reasons that must both stay true:
 *
 *   a. This is still a very large improvement on what it replaces. What ships
 *      today paints every EV the one identical green - ΔE 0.0 between every
 *      pair, for every reader. Going from 0.0 to a worst case of 3.2 fixes the
 *      reported defect for the great majority of readers and makes it no worse
 *      for anybody.
 *   b. SECONDARY ENCODING EXISTS AND MUST KEEP EXISTING. The Summary and
 *      Storage charts both render a legend naming every series beside its
 *      swatch, and both tooltips print the series name with every value, so a
 *      device's identity is never carried by colour alone. Delete that and
 *      these pairs become unreadable for red-green CVD readers.
 *
 * Contrast against the light surface is below 3:1 for #5BC840 (2.09), #2DAEA0
 * (2.67), #00A5FF (2.61) and #E17AF8 (2.45). That is a documented conditional
 * relax rather than a hard fail, discharged by the same named legend and
 * tooltips as (b).
 *
 * Adding the two production bases (below) takes the co-plotted set to twelve
 * colours and 78 pairs. In normal vision every pair involving a production base
 * clears 15: the weakest is Solar #963800 ↔ Consumption #FF1E1E at ΔE 20.79,
 * then Generator #0000F0 ↔ Transfer #675AFF at ΔE 17.66. The worst pair in the
 * whole twelve is still #A36FAF ↔ #9628A1 at ΔE 14.947 - the production bases
 * are no longer the weakest link in the chart, which is what they were.
 *
 * Under red-green CVD both bases are a large improvement rather than another
 * accepted failure: Solar's worst is ΔE 9.0 protan / 9.6 deutan (was 3.3 / 2.3
 * against Production Summary) and Generator's worst is ΔE 15.6 / 17.2 (was
 * 1.9 / 2.6 against #9628A1). Those two CVD figures were computed here with a
 * Viénot-1999 dichromat simulation and the same OKLab ΔE, NOT with
 * scripts/validate_palette.js, which is not installed on the machine this was
 * measured on - re-run the validator over all twelve before trusting them to
 * the digit. The normal-vision figures above ARE the validator's metric.
 *
 * ── Index 0 is the caller's base for storage, but NOT for EV ──────────────
 * The earlier round kept index 0 on whatever colour the chart already gave the
 * category, so a single-device chart stayed pixel-identical and nothing
 * churned. EV DELIBERATELY GIVES THAT UP: the first EV is now #5BC840 rather
 * than the inherited #91CC75, because #91CC75 is not part of the validated set
 * and on its own it fails the lightness band (L 0.784 against 0.43-0.77). The
 * churn is accepted, and the tests that pinned #91CC75 were updated to the new
 * colour rather than loosened.
 *
 * BESS keeps the base-at-index-0 shape, but both charts now pass the SAME base
 * - #0072B2. They used to disagree, "#d7ea10ff" on Summary/OverlayChart against
 * "#5470C6" on StorageDashboard/StorageChart, so one battery had two colours
 * depending on which page you opened. #0072B2 is measurably the better of the
 * three against the summary lines: worst normal-vision separation ΔE 15.6,
 * against 14.1 for #d7ea10ff and 10.5 for #5470C6.
 *
 * ── One continuation sequence, shared by every category ───────────────────
 * A second BESS, Solar or Generator device continues into the EV sequence
 * rather than getting a bespoke list of its own. Bespoke lists are how the
 * first attempt produced a ΔE 1.0 pair: separating four categories needs far
 * more colours than survive validation, and every colour drawn here should be
 * one that was actually measured. The consequence, stated plainly: a chart with
 * a second BESS AND a first EV gives both #5BC840. That is accepted because
 * separation WITHIN a category is what the report was about, and because the
 * legend names both. Past the end of the list colours repeat rather than being
 * generated - a generated hue is never a validated one, and cycling is the
 * documented behaviour once a chart exceeds what colour can distinguish.
 *
 * ── The two production bases, measured at last ────────────────────────────
 * An earlier round of this file left the Solar and Generator bases on their
 * inherited values and excused them here as "outside the validated set". That
 * excuse described the paperwork, not the pixels. OverlayChart's legendItems
 * draws a solar array, a generator, every enabled EV, the battery and all four
 * summary lines onto ONE axis, so a production base overlaps other strokes in
 * exactly the way an EV does - and the palette test swept only the EV sequence,
 * so nothing measured either base. Measuring them found two collisions worse
 * than the ΔE 1.0 pair this module was written to fix:
 *
 *   Generator #8E24AA ↔ EV #9628A1        ΔE 2.45   (normal vision)
 *   Solar     #FF9D41 ↔ Production #E6A700 ΔE 5.99   (normal vision)
 *
 * Both are now moved onto measured values, and both are swept against the EV
 * sequence, the battery, the four summary lines and each other at the SAME
 * floors an EV is held to - EV 14.9, battery 16.8, summary 15.3. There is no
 * relaxed floor for a production base; the solar/gold pair did NOT need one.
 *
 *   Generator #8E24AA → #0000F0, a deep blue-violet. Worst pair ΔE 17.66
 *     against Transfer #675AFF; battery 20.73; nearest EV 27.20 (#9628A1).
 *     L 0.432 and hue 264.1°, so it is inside the validator's lightness band
 *     and outside the excluded warm half of the wheel. Contrast against the
 *     light surface 9.30:1. The purple neighbourhood #8E24AA came from cannot
 *     be repaired in place: the EV sequence already spends three entries on
 *     purples between 319.9° and 324.0° (see "Three purples" above), so a
 *     sweep of every in-band purple at chroma ≥ 0.10 tops out at ΔE 15.44
 *     (#5802C8) and every candidate above ΔE 16 in that band collapses under
 *     red-green CVD - the best of them sits at ΔE 1.5 deutan. The blue-violet
 *     was chosen over a brighter magenta at a comparable normal-vision score
 *     for exactly that reason: it is the only region that keeps the pair apart
 *     for CVD readers too.
 *
 *   Solar #FF9D41 → #963800, a burnt orange. Worst pair ΔE 20.79 against
 *     Consumption #FF1E1E; Production Summary now 31.62 rather than 5.99;
 *     nearest EV 22.24. Solar stays WARM deliberately: the cool half of the
 *     wheel is spent on six EVs, the battery, Transfer and Storage Summary,
 *     and constraint 1 above keeps the EVs out of the warm half, so warm is
 *     where a production base can actually live. What moves is lightness. The
 *     old #FF9D41 sat at L 0.781, outside the 0.43-0.77 band, at 2.07:1
 *     against the light surface; #963800 is L 0.467 and 7.34:1. Gold and
 *     orange only separate by lightness here - every in-band warm colour that
 *     clears the floors is darker than the gold summary line, which is what
 *     "burnt" buys.
 *
 * Neither base is anchored: index 1 onwards still continues into DEVICE_PALETTE.
 * Both moves are visible churn on a single-device production chart, accepted for
 * the same reason the first EV gave up #91CC75 - the inherited value was not
 * measurable, and now it is.
 */

/* The EV sequence. Index 0 IS this list's first entry - the caller's base is
 * not consulted for EV. Ordered so the two pairs that fail protan/deutan
 * against the battery blue (#A36FAF, #9628A1) are reached as late as the
 * light-green-first constraint allows, and so the ΔE 4.3 protan pair
 * (#00A5FF ↔ #E17AF8) needs five enabled EVs before both are on screen. */
const EV_SEQUENCE = [
  "#5BC840", // light green   - first by request
  "#2DAEA0", // teal
  "#00A5FF", // cyan-blue     - ΔE 4.3 protan against #E17AF8 below
  "#A36FAF", // purple        - ΔE 4.0 protan against battery #0072B2
  "#E17AF8", // light magenta - lightness-separated from the two purples
  "#9628A1", // deep violet   - ΔE 3.2 deutan against battery #0072B2
];

/* Where a non-EV category goes for its SECOND device onwards; its first device
 * keeps the base colour the chart passes in. The EV sequence is reused rather
 * than duplicated - see "One continuation sequence" above. */
const DEVICE_PALETTE = EV_SEQUENCE;

/* Categories whose index 0 is fixed by this module instead of inherited from
 * the caller. Only EV, and only because its inherited #91CC75 is unvalidated. */
const ANCHORED_SEQUENCES = { EV: EV_SEQUENCE };

/**
 * The line colour for one device.
 *
 * @param {string} baseColor - the colour this chart gives the category. Used
 *   for the first device of a non-anchored category (BESS, Solar, Generator),
 *   so those single-device charts stay pixel-identical. IGNORED for EV, which
 *   is anchored on #5BC840 - see the note above.
 * @param {number} index - the device's position in its category's roster.
 * @param {string} [category] - "EV" to use the anchored EV sequence. Omitted or
 *   unrecognised means base-at-index-0 behaviour.
 * @returns {string} a CSS colour.
 */
export function getDeviceColor(baseColor, index, category) {
  const position = Number.isInteger(index) && index > 0 ? index : 0;
  const anchored = ANCHORED_SEQUENCES[category];
  if (anchored) return anchored[position % anchored.length];
  if (position === 0) return baseColor;
  return DEVICE_PALETTE[(position - 1) % DEVICE_PALETTE.length];
}

/** The colour both charts give a battery. Shared so they cannot drift apart
 * again - they disagreed before this, which is the mismatch it fixes. */
export const BESS_BASE_COLOR = "#0072B2";

/** The colours a chart gives its FIRST solar array and its FIRST generator.
 * Exported for the same reason as BESS_BASE_COLOR: ProductionChart painted
 * these two literals on EVERY device of the category, so a customer with two
 * solar arrays saw indistinguishable overlapping strokes there while the
 * Summary page separated the same two devices correctly - the defect at the top
 * of this file, at the production roster. Index 1 onwards continues into
 * DEVICE_PALETTE.
 *
 * Both values are now measured against the whole co-plotted set rather than
 * inherited - see "The two production bases, measured at last" above. They
 * replace #FF9D41 (ΔE 5.99 from the Production Summary line) and #8E24AA
 * (ΔE 2.45 from the EV #9628A1). Do not move either by eye; the palette test
 * sweeps both at the same floors it holds an EV to. */
export const SOLAR_BASE_COLOR = "#963800";
export const GENERATOR_BASE_COLOR = "#0000F0";

/** The four Summary lines, and the one place they are written down.
 *
 * Every ΔE figure in this file's header is quoted against these four - the EV
 * sequence, the battery and both production bases are all swept against them -
 * so the number a test measures and the pixel a chart paints have to be the
 * same string. They were not: the sweep in deviceColors.test.js held a
 * hand-copied array of its own, and each chart carried its own literals, so the
 * floor was measured against a private copy that no chart consumed. Moving a
 * chart's summary colour passed the palette suite untouched, and moving the
 * measured one reached no chart. That is the structural reason the two
 * production-base collisions (ΔE 2.45 and 5.99) could sit live on OverlayChart
 * with 975 tests green.
 *
 * Notation matters too: OverlayChart wrote Consumption as `rgba(255, 30, 30, 1)`
 * while every measurement here quotes #FF1E1E. Same colour, but the test's
 * hex parser cannot read the rgba form, so a drift to it would have been
 * unmeasurable rather than merely unmeasured. The hex is the canonical form and
 * charts import it.
 *
 * Values are unchanged from what shipped; only their home is new. */
export const TRANSFER_LINE_COLOR = "#675AFF";
export const CONSUMPTION_LINE_COLOR = "#FF1E1E";
export const STORAGE_SUMMARY_LINE_COLOR = "#1B7F5A";
export const PRODUCTION_SUMMARY_LINE_COLOR = "#E6A700";

/** The four above as the co-plotted set, in the order the header quotes them.
 * The palette sweep imports THIS rather than restating it. */
export const SUMMARY_LINE_COLORS = [
  TRANSFER_LINE_COLOR,
  CONSUMPTION_LINE_COLOR,
  STORAGE_SUMMARY_LINE_COLOR,
  PRODUCTION_SUMMARY_LINE_COLOR,
];

/** Exposed so a test can pin the sequence against an unvalidated edit. */
export const DEVICE_COLOR_SEQUENCE = DEVICE_PALETTE;

/** Exposed for the same reason, and to name the anchored EV order explicitly. */
export const EV_COLOR_SEQUENCE = EV_SEQUENCE;
